import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Minus, Plus, ShoppingCart, ArrowLeft, Check, ShieldCheck, Truck, Lock, Sparkles } from "lucide-react";
import { api, formatARS, isNewProduct } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const { addItem } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => setProduct(r.data)).catch(() => setProduct(false));
  }, [id]);

  if (product === null) return <div className="p-20 text-center text-slate-500">Cargando...</div>;
  if (product === false) return (
    <div className="p-20 text-center">
      <p className="text-slate-500">Producto no encontrado.</p>
      <Link to="/shop" className="mt-4 inline-block text-blue-600">Volver a la tienda</Link>
    </div>
  );

  const out = product.stock <= 0;
  const canSeePrice = !!user;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="product-detail-page">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-slate-600 hover:text-blue-600 mb-6" data-testid="back-btn">
        <ArrowLeft className="w-4 h-4" /> Volver
      </button>
      <div className="grid md:grid-cols-2 gap-10">
        <div className="bg-white rounded-3xl border border-slate-200 p-8 aspect-square flex items-center justify-center overflow-hidden">
          <img src={product.image} alt={product.name} className="max-h-full max-w-full object-contain rounded-2xl" />
        </div>
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-sm font-semibold uppercase tracking-[0.2em] text-blue-600" data-testid="pd-category">{product.category}</span>
            {isNewProduct(product) && (
              <span className="inline-flex items-center gap-1 bg-green-600 text-white text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full" data-testid="pd-new-badge">
                <Sparkles className="w-3 h-3" /> Nuevo
              </span>
            )}
          </div>
          <h1 className="font-heading font-extrabold text-3xl md:text-4xl tracking-tight mt-2" data-testid="pd-name">{product.name}</h1>

          {canSeePrice ? (
            <div className="mt-4 flex items-baseline gap-3">
              <span className="text-4xl font-bold text-slate-900" data-testid="pd-price">{formatARS(product.price)}</span>
              {product.stock > 0 ? (
                <span className="text-sm text-green-600 flex items-center gap-1"><Check className="w-4 h-4" /> En stock ({product.stock})</span>
              ) : (
                <span className="text-sm text-red-500">Sin stock</span>
              )}
            </div>
          ) : (
            <div className="mt-4 p-4 rounded-2xl bg-blue-50 border border-blue-100 flex items-center gap-3">
              <Lock className="w-5 h-5 text-blue-600 flex-shrink-0" />
              <div className="flex-1 text-sm text-slate-700">
                <strong>Iniciá sesión</strong> para ver el precio y comprar.
              </div>
              <Link to="/login" className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-full px-4 py-2 transition-colors">Ingresar</Link>
            </div>
          )}

          <p className="text-slate-600 leading-relaxed mt-6" data-testid="pd-description">{product.description}</p>

          {canSeePrice && (
            <div className="mt-8 flex items-center gap-4">
              <div className="flex items-center border border-slate-200 rounded-full">
                <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="p-3 hover:bg-slate-50 rounded-l-full" data-testid="qty-decrease"><Minus className="w-4 h-4" /></button>
                <span className="px-4 font-semibold min-w-[2rem] text-center" data-testid="qty-value">{qty}</span>
                <button onClick={() => setQty((q) => q + 1)} className="p-3 hover:bg-slate-50 rounded-r-full" data-testid="qty-increase"><Plus className="w-4 h-4" /></button>
              </div>
              <button
                onClick={() => addItem(product, qty)}
                disabled={out}
                className="btn-primary flex-1"
                data-testid="pd-add-to-cart"
              >
                <ShoppingCart className="w-4 h-4" /> Agregar al carrito
              </button>
            </div>
          )}

          <div className="mt-8 grid grid-cols-2 gap-4">
            <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl">
              <Truck className="w-5 h-5 text-blue-600" />
              <div className="text-sm"><div className="font-semibold">Envíos</div><div className="text-slate-500">A todo el país</div></div>
            </div>
            <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl">
              <ShieldCheck className="w-5 h-5 text-green-600" />
              <div className="text-sm"><div className="font-semibold">Garantía</div><div className="text-slate-500">Calidad asegurada</div></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
